/**
 * Which fitting guide the support page shows for a saved vehicle (RAZ-328).
 *
 * A guide is a `fitting_guide` metaobject. The content team links it from a model, a
 * series or a single fitment, and the most specific link wins: fitment, then series, then
 * model. A vehicle with no link gets no guide: the block says so rather than showing
 * someone else's.
 *
 * Any of those levels can instead carry a "Fitting guide redirect" URL, for a guide that
 * lives off the page (a help centre article, say). It takes that level's place in the same
 * order, so a series' guide still beats its model's redirect, and the other way round.
 * Where one entry has both, Liquid has already let the redirect win.
 *
 * The lookup is written by blocks/rb-fitting-guide.liquid, which reads the links straight
 * off the metaobjects on every page render. It is not part of the cached vehicle catalogue,
 * so a guide linked in the admin shows as soon as Shopify's page cache turns over.
 *
 * The same rule is mirrored, in a few lines of ES5, by the block's pre-paint script so the
 * first frame already shows the right panel. Change one, change the other.
 */

/**
 * What a level links to: a guide handle, or `{ url }` for a redirect.
 * @typedef {string|{url: string}} GuideLink
 */

/**
 * @typedef {Object} GuideLookup
 * @property {Object<string, GuideLink>} f fitment id → its own link, or its series' link
 * @property {Object<string, GuideLink>} s series handle → link
 * @property {Object<string, GuideLink>} m model handle → link
 * @property {Array<string>} guides handles of every guide rendered on the page
 */

/**
 * @typedef {Object} PickedGuide
 * @property {string|null} handle the guide to show, or null for none
 * @property {string} [url] present only when the vehicle's guide is a redirect
 * @property {'fitment'|'candidates'|'series'|'model'|'missing'|'none'} source why: which
 *   link matched ('candidates' when an incomplete vehicle's fitments all share one link),
 *   'missing' for a vehicle with no guide of its own, 'none' when no vehicle is saved
 */

/** Handles are compared case-insensitively, the way vehicleResolver.js folds them. */
function fold(value) {
  return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
}

/**
 * The redirect URL a link carries, or '' when it is not a redirect.
 * @param {*} link
 * @returns {string}
 */
function redirectOf(link) {
  return link && typeof link === 'object' && typeof link.url === 'string' ? link.url.trim() : '';
}

/**
 * One comparable string per link, so candidates can be checked for agreement. A guide and
 * a redirect never compare equal, nor do two different redirects.
 * @param {*} link
 * @returns {string} '' for anything that is not a usable link
 */
function linkKey(link) {
  const url = redirectOf(link);
  if (url !== '') return `url:${url}`;
  return typeof link === 'string' && link !== '' ? `guide:${link}` : '';
}

/**
 * A lookup map re-keyed by folded key, ignoring anything that is not a usable link.
 * @param {*} map
 * @returns {Map<string, GuideLink>}
 */
function foldedMap(map) {
  const folded = new Map();
  if (!map || typeof map !== 'object' || Array.isArray(map)) return folded;
  Object.entries(map).forEach(([key, link]) => {
    if (linkKey(link) !== '') folded.set(fold(key), link);
  });
  return folded;
}

/**
 * Pick the guide for a saved vehicle.
 *
 * Only guide handles in `lookup.guides` are ever returned: a link to a guide that has
 * since been deleted would otherwise select a panel that does not exist, and the shopper
 * would see nothing where the next-best guide belonged. A redirect needs no panel.
 *
 * @param {GuideLookup|null} lookup
 * @param {Object|null} vehicle the saved vehicle (see buildVehicle() in vehicleResolver.js)
 * @returns {PickedGuide}
 */
export function pickGuide(lookup, vehicle) {
  const rendered = new Set(Array.isArray(lookup?.guides) ? lookup.guides : []);

  const model = fold(vehicle?.model?.h);
  if (model === '') return { handle: null, source: 'none' };

  const byFitment = foldedMap(lookup?.f);
  const links = [
    ['fitment', byFitment.get(fold(vehicle.id))],
    ['candidates', sharedLink(byFitment, vehicle.candidates)],
    ['series', foldedMap(lookup?.s).get(fold(vehicle.series?.h))],
    ['model', foldedMap(lookup?.m).get(model)],
  ];

  for (const [source, link] of links) {
    const url = redirectOf(link);
    if (url !== '') return { handle: null, url, source };
    if (typeof link === 'string' && rendered.has(link)) return { handle: link, source };
  }
  return { handle: null, source: 'missing' };
}

/**
 * The link every candidate fitment shares, or undefined when they disagree.
 *
 * The Year-first finder saves as soon as every candidate shares a destination, which is
 * often before the variant is known: a 2007 HiLux saves with no fitment id and no series.
 * Liquid writes each fitment's link into `f`, falling back to its series' link, so a
 * vehicle whose candidates all sit in one generation still reaches that generation's guide.
 *
 * @param {Map<string, GuideLink>} byFitment folded fitment id → link
 * @param {*} candidates the saved vehicle's candidate fitment ids
 * @returns {GuideLink|undefined}
 */
function sharedLink(byFitment, candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) return undefined;
  const links = candidates.map((id) => byFitment.get(fold(id)));
  if (links.some((link) => link === undefined)) return undefined;
  const first = linkKey(links[0]);
  return links.every((link) => linkKey(link) === first) ? links[0] : undefined;
}
